/* eslint-disable max-lines */

import React, { useEffect, useState } from 'react';
import * as Ymaps from 'yandex-maps';
import { StoresList } from 'features/stores';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { analyticsUtils } from 'utils';
import { Store } from 'features/stores/types';
import {
  CENTER_DEFAULT,
  ZOOM_DEFAULT,
  Z_INDEX_ACTIVE_PLACEMARK,
  Z_INDEX_ACTIVE_PLACEMARK_HOVER,
  Z_INDEX_PLACEMARK_HOVER,
  clusterIcon,
  createPlacemark,
  hoverLayout,
  placemarkIcon,
  placemarkIconActive,
  setMapCenter,
} from './utils';
import { styles } from './style';

type Props = {
  storeList: StoresList | null;
  regionId: string | undefined;
  activeStore: Store | null;
  activeRegionId: string | undefined;
  setActiveStore: (store: Store | null) => void;
  setActiveRegionId: (id: string) => void;
};

export const Map: React.FC<Props> = ({
  storeList,
  activeStore,
  activeRegionId = '34',
  regionId = '34',
  setActiveStore,
  setActiveRegionId,
}) => {
  const { desktop, largeDesktop } = useBreakpoints() || {};
  const isDesktop = desktop || largeDesktop;
  const [map, setMap] = useState<Ymaps.Map | null>(null);
  const [activePlacemark, setActivePlacemark] = useState<Ymaps.Placemark | null>(null);

  useEffect(() => {
    const { ymaps } = window;
    const activeLayout = ymaps.templateLayoutFactory?.createClass(placemarkIconActive);
    if (map) {
      if (activeStore) {
        const placemark = createPlacemark(
          activeRegionId,
          activeStore,
          'store-point-active-region',
          activeLayout,
          null,
          Z_INDEX_ACTIVE_PLACEMARK_HOVER,
          Z_INDEX_ACTIVE_PLACEMARK,
        );

        setMapCenter(map, activeStore, isDesktop);

        if (activePlacemark) {
          map.geoObjects.remove(activePlacemark);
        }

        setActivePlacemark(placemark);
        map.geoObjects.add(placemark);

        placemark.events.add('click', () => {
          setActiveStore(null);
        });
      } else if (activePlacemark) {
        map.geoObjects.remove(activePlacemark);
        setActivePlacemark(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegionId, activeStore]);

  useEffect(() => {
    if (map && storeList) {
      const region = storeList[activeRegionId];
      setMapCenter(map, region, isDesktop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegionId]);

  useEffect(() => {
    const { ymaps } = window;
    ymaps.ready(init);
    function init() {
      const { Clusterer, templateLayoutFactory } = ymaps;
      const clusterIconContentLayout = templateLayoutFactory?.createClass(clusterIcon);
      const iconContentLayout = templateLayoutFactory?.createClass(placemarkIcon);
      const balloonLayout = templateLayoutFactory?.createClass(hoverLayout);

      const cluster: unknown = new Clusterer({
        clusterIcons: [
          {
            size: [48, 48],
            offset: [0, 0],
          },
        ],
        clusterIconContentLayout,
      } as Ymaps.IClustererOptions);

      (cluster as ymaps.Clusterer).events.add('click', (event: Ymaps.IEvent) => {
        const geoObjects = event.get('target').properties.get('geoObjects');
        if (geoObjects) {
          const id = geoObjects[0].properties.get('regionId');
          setActiveRegionId(id);
        }
      });

      if (storeList) {
        Object.entries(storeList).forEach(([id, region]) => {
          if (region?.stores?.length) {
            region.stores.forEach((store) => {
              const placemark = createPlacemark(
                id,
                store,
                'store-point-region',
                iconContentLayout,
                balloonLayout,
                Z_INDEX_PLACEMARK_HOVER,
              );

              if (placemark) {
                placemark.events.add('click', () => {
                  setActiveRegionId(id);
                  setActiveStore(store);
                  analyticsUtils.sendEvent('GA:clickButton', {
                    elementType: 'button',
                    elementBlock: 'shopPage',
                    elementText: store.name, // название магазина
                    elementName: 'map', // название блока
                  });
                });

                placemark.events.add('mouseenter', () => {
                  placemark.balloon.open();
                });

                placemark.events.add('mouseleave', () => {
                  placemark.balloon.close();
                });

                (cluster as ymaps.Clusterer).add(placemark);
              }
            });
          }
        });

        const lat = Number(storeList[regionId]?.lat || CENTER_DEFAULT[0]);
        const long = Number(storeList[regionId]?.long || CENTER_DEFAULT[1]);
        const mapZoom = Number(storeList[regionId]?.mapZoom || ZOOM_DEFAULT);

        const mapInstance = new ymaps.Map(
          'map',
          {
            center: [lat, long],
            zoom: mapZoom,
            controls: [],
          },
          { suppressMapOpenBlock: true },
        );

        const zoomControl = new ymaps.control.ZoomControl({
          options: {
            size: 'small',
            position: {
              right: 12,
              bottom: 40,
            },
          },
        });

        mapInstance.controls.add(zoomControl);

        mapInstance.geoObjects.add(cluster as Ymaps.IGeoObject);

        if (isDesktop) {
          // 400 is a hand-picked value based on stores list width
          mapInstance.margin.setDefaultMargin([10, 10, 10, 400]);

          let pixelCenter = mapInstance.getGlobalPixelCenter();
          // 200 is a hand-picked value based on stores list width
          pixelCenter = [pixelCenter[0] - 200, pixelCenter[1]];
          const shiftedCenter = (
            mapInstance.options.get('projection', [lat, long]) as Ymaps.IProjection
          ).fromGlobalPixels(pixelCenter, mapInstance.getZoom());
          mapInstance.setCenter(shiftedCenter);
        }

        setMap(mapInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeList, regionId]);
  return <div id="map" className={styles} data-qa="stores-map" />;
};
